"use client"

import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback } from "react"
import { Baby, Home, Users, PawPrint, Utensils, Palette, Plane, HandCoins, Star, MessageSquare, X, Lock, Crown } from "lucide-react"

/**
 * Boolean amenity + caregiver-friendly filters for the directory. These map to
 * the query params getResidencies() already reads (childcareAvailable,
 * familyHousing, partnerWelcome, petFriendly, housing, meals, studioSpace,
 * travelCovered, funded); previously there was no UI to set them. Funding cost
 * type + discipline + location live in the segmented search bar above.
 *
 * The Ratings group covers the discovery queries reviews power ("funded
 * residencies in Europe rated 4+"): minRating is a value chip (3 / 4 are
 * mutually exclusive on the same param), hasReviews is a plain toggle.
 *
 * All of these are Pro-only (see lib/directoryFilters.ts for the gate and the
 * server-side enforcement). Non-Pro visitors still see every chip — locked,
 * and routing to /pro rather than filtering — because the chips themselves are
 * the pitch: you can't want a childcare filter you never knew existed.
 */

type ChipDef = { key: string; label: string; icon: React.ReactNode; value?: string } // value defaults to "true"

const GROUPS: { heading: string; chips: ChipDef[] }[] = [
  {
    heading: "Family & access",
    chips: [
      { key: "childcare", label: "Childcare", icon: <Baby size={13} /> },
      { key: "familyHousing", label: "Family housing", icon: <Home size={13} /> },
      { key: "partnerWelcome", label: "Partner welcome", icon: <Users size={13} /> },
      { key: "petFriendly", label: "Pet friendly", icon: <PawPrint size={13} /> },
    ],
  },
  {
    heading: "What's provided",
    chips: [
      { key: "funded", label: "Funded", icon: <HandCoins size={13} /> },
      { key: "housing", label: "Housing", icon: <Home size={13} /> },
      { key: "meals", label: "Meals", icon: <Utensils size={13} /> },
      { key: "studio", label: "Studio", icon: <Palette size={13} /> },
      { key: "travelCovered", label: "Travel covered", icon: <Plane size={13} /> },
    ],
  },
  {
    heading: "Ratings",
    chips: [
      { key: "minRating", value: "4", label: "Rated 4+", icon: <Star size={13} /> },
      { key: "minRating", value: "3", label: "Rated 3+", icon: <Star size={13} /> },
      { key: "hasReviews", label: "Has reviews", icon: <MessageSquare size={13} /> },
    ],
  },
]

const ALL_KEYS = [...new Set(GROUPS.flatMap((g) => g.chips.map((c) => c.key)))]

const CHIP_BASE =
  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[12px] transition-colors whitespace-nowrap"

export function DirectoryFilterBar({ isPro = false }: { isPro?: boolean }) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const isOn = useCallback(
    (chip: ChipDef) => searchParams.get(chip.key) === (chip.value ?? "true"),
    [searchParams]
  )
  const activeCount = ALL_KEYS.filter((k) => searchParams.get(k) !== null).length

  const toggle = useCallback(
    (chip: ChipDef) => {
      const next = new URLSearchParams(searchParams.toString())
      const value = chip.value ?? "true"
      if (next.get(chip.key) === value) next.delete(chip.key)
      else next.set(chip.key, value)
      next.delete("page") // filtering changes the result set — back to page 1
      router.push(`/directory?${next.toString()}`)
    },
    [router, searchParams]
  )

  const clearAll = useCallback(() => {
    const next = new URLSearchParams(searchParams.toString())
    for (const k of ALL_KEYS) next.delete(k)
    next.delete("page")
    router.push(`/directory?${next.toString()}`)
  }, [router, searchParams])

  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {GROUPS.map((group) => (
          <div key={group.heading} className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-[var(--muted-foreground)] mr-0.5">
              {group.heading}
            </span>
            {group.chips.map((chip) => {
              const key = `${chip.key}-${chip.value ?? "true"}`

              // Locked state: same chip, same place, but it sells the plan
              // instead of filtering. A link (not a disabled button) so it's
              // keyboard-reachable and crawlable.
              if (!isPro) {
                return (
                  <Link
                    key={key}
                    href="/pro?from=directory-filters"
                    title={`${chip.label} is a Pro filter — upgrade for $9/mo`}
                    className={`${CHIP_BASE} bg-white text-[var(--muted-foreground)]/70 border-dashed border-[var(--border)] hover:border-[var(--foreground)] hover:text-[var(--foreground)]`}
                  >
                    {chip.icon}
                    {chip.label}
                    <Lock size={10} className="opacity-60" aria-hidden />
                    <span className="sr-only">(Pro only)</span>
                  </Link>
                )
              }

              const on = isOn(chip)
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggle(chip)}
                  aria-pressed={on}
                  className={`${CHIP_BASE} ${
                    on
                      ? "bg-[var(--foreground)] text-white border-[var(--foreground)]"
                      : "bg-white text-[var(--muted-foreground)] border-[var(--border)] hover:border-[var(--foreground)] hover:text-[var(--foreground)]"
                  }`}
                >
                  {chip.icon}
                  {chip.label}
                </button>
              )
            })}
          </div>
        ))}
        {isPro && activeCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-1 text-[12px] text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
          >
            <X size={12} /> Clear {activeCount === 1 ? "filter" : "filters"}
          </button>
        )}
      </div>

      {!isPro && (
        <p className="mt-2 text-[12px] text-[var(--muted-foreground)]">
          <Crown size={11} className="inline-block mb-0.5 mr-1 text-amber-500" aria-hidden />
          Advanced filters are a{" "}
          <Link href="/pro?from=directory-filters" className="text-[var(--foreground)] font-medium underline underline-offset-4">
            Pro
          </Link>{" "}
          feature. Search by name, discipline, location, and funding is free for everyone.
        </p>
      )}
    </div>
  )
}
